import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BaseServiceWithFilter } from './base.service';
import { CommandResult } from '@cl/shared/models/misc/command-result.model';
import { lastValueFrom, Observable } from 'rxjs';
import { PerformerFilter } from '@cl/shared/models/performers/performer-filter.model';
import { Performer } from '@cl/shared/models/performers/performer.model';
import { PerformersForHrOrder } from '@cl/shared/models/performers/performers-for-hr-order.model';

@Injectable({ providedIn: 'root' })
export class PerformersService extends BaseServiceWithFilter<Performer, PerformerFilter> {
    constructor(httpClient: HttpClient) {
        super(httpClient, '/api/v1/performers/');
    }

    getUnlinked(): Observable<CommandResult<Performer[]>> {
        return this.httpClient.get<CommandResult<Performer[]>>(this.baseApiUrl + 'unlinked');
    }

    getCompanyPerformersByDepartment(departmentId: number): Observable<CommandResult<Performer[]>> {
        return this.httpClient.get<CommandResult<Performer[]>>(`${this.baseApiUrl}department-company-performers/${departmentId}`);
    }

    getCompanyPerformers(companyId: number): Observable<CommandResult<Performer[]>> {
        return this.httpClient.get<CommandResult<Performer[]>>(`${this.baseApiUrl}company-performers/${companyId}`);
    }

    isPersonCodeUnique(code: string, id: number): Observable<boolean> {
        return this.httpClient.get<boolean>(`${this.baseApiUrl}${code}/unique/${id}`);
    }

    searchBetweenPerformersRelatedToUser(text: string): Observable<CommandResult<Performer[]>> {
        return this.httpClient.get<CommandResult<Performer[]>>(`${this.baseApiUrl}search-related/${text}`);
    }

    getAllUsersPerformers(): Observable<CommandResult<Performer[]>> {
        return this.httpClient.get<CommandResult<Performer[]>>(`${this.baseApiUrl}users-performers`);
    }

    async getAllRelatedPerformerIds(): Promise<CommandResult<number[]>> {
        return await lastValueFrom(this.httpClient.get<CommandResult<number[]>>(`${this.baseApiUrl}all-related-ids`));
    }

    getDepartmentPerformers(departmentId: number): Observable<CommandResult<Performer[]>> {
        return this.httpClient.get<CommandResult<Performer[]>>(`${this.baseApiUrl}department-performers/${departmentId}`);
    }

    getAllUserDepartmentPerformers(): Observable<CommandResult<Performer[]>> {
        return this.httpClient.get<CommandResult<Performer[]>>(`${this.baseApiUrl}all-users-department-performers`);
    }

    getAllUsersCompaniesPerformers(): Observable<CommandResult<Performer[]>> {
        return this.httpClient.get<CommandResult<Performer[]>>(`${this.baseApiUrl}all-users-companies-performers`);
    }

    getPerformersAccessibleByDepartmentPermission(departmentPermissionId: number): Observable<CommandResult<Performer[]>> {
        return this.httpClient.get<CommandResult<Performer[]>>(`${this.baseApiUrl}accessible-by-department-permission/${departmentPermissionId}`);
    }

    toString(object: Performer): string {
        if (!object.departmentName) {
            return object.fullName;
        }

        return `${object.fullName} (${object.departmentName})`;
    }

    toStringWithCompany(performer: Performer): string {
        return `${performer.fullName} (${performer.companyName} - ${performer.departmentName})`;
    }

    // HR-Orders formas iesniedzēji un darbinieki vienā īpašnieka atbildē — lietotāja izmantojamā kopa; pēc uzņēmuma filtrē forma.
    // Divas atšķirīgas kopas: iesniedzēja darba vietas (paša performer rindas) un darbinieku kandidāti.
    async getForHrOrder(): Promise<PerformersForHrOrder> {
        const result = await lastValueFrom(this.httpClient.get<CommandResult<PerformersForHrOrder>>(`${this.baseApiUrl}for-hr-order`));

        return result?.data ?? { submitterPerformers: [], employees: [] };
    }

    // Saskaņotāja izvēle tikai no servera kandidātiem — to pašu definīciju serveris validē saglabājot.
    async getHrOrderCoordinatorCandidates(hrOrderId: number): Promise<Performer[]> {
        const result = await lastValueFrom(
            this.httpClient.get<CommandResult<Performer[]>>(`${this.baseApiUrl}hr-order-coordinator-candidates/${hrOrderId}`),
        );

        return result?.data ?? [];
    }
}
